import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Spinner, Alert, Table } from 'react-bootstrap';
import Select from 'react-select';
import { Download } from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import {
  csvNumber,
  downloadCsv,
  formatDate,
  formatNumber,
  getAssetAccountingUnavailable,
  getCategoryMeta,
  toCsvValue,
  unwrapData,
} from '../../Accounting/Assets/assetUtils';

const num = (value) => parseFloat(value) || 0;

// ÅRL 5:8 / K2 punkt 18.10-formen: anskaffningsvärden IB → inköp →
// försäljningar/utrangeringar → UB, samma rörelse för avskrivningarna,
// sist redovisat värde. En rad per kategori + summarad.
const COLUMNS = [
  { key: 'ib_acquisition', label: 'IB anskaffningsvärden' },
  { key: 'additions', label: 'Årets inköp' },
  { key: 'disposals', label: 'Försäljningar/utrangeringar' },
  { key: 'ub_acquisition', label: 'UB anskaffningsvärden' },
  { key: 'ib_depreciation', label: 'IB avskrivningar' },
  { key: 'year_depreciation', label: 'Årets avskrivningar' },
  { key: 'disposal_depreciation', label: 'Avskrivningar på sålt/utrangerat' },
  { key: 'ub_depreciation', label: 'UB avskrivningar' },
  { key: 'book_value', label: 'Redovisat värde' },
];

const categoryLabel = (row) => {
  const meta = getCategoryMeta(row.category_code || row.category);
  return row.category_name || meta.label;
};

/**
 * AssetNoteReport — "Tillgångsnot": K2-notens tabell över materiella
 * anläggningstillgångar per kategori för ett räkenskapsår, byggd från
 * anläggningsregistret (GET /assets/reports/note). Klient-genererad CSV.
 */
const AssetNoteReport = () => {
  const { companyId } = useParams();

  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loadingYears, setLoadingYears] = useState(true);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await axios.get(
          getApiUrl(`${companyId}/accounting/fiscalyears`),
          { withCredentials: true }
        );
        const years = result.data?.fiscalYears || [];
        if (cancelled) return;
        const sorted = [...years].sort((a, b) => b.year - a.year);
        setFiscalYears(sorted);
        if (sorted.length > 0) {
          const today = new Date();
          const ended = sorted.find((fy) => new Date(fy.end_date) <= today);
          setSelectedYear(ended || sorted[0]);
        }
      } catch (err) {
        if (!cancelled) setError('Kunde inte hämta räkenskapsår.');
      } finally {
        if (!cancelled) setLoadingYears(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  const fetchReport = useCallback(async (fiscalYearId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/assets/reports/note?fiscalYearId=${fiscalYearId}`),
        { withCredentials: true }
      );
      const data = unwrapData(response) || {};
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotals(data.totals || null);
    } catch (err) {
      setRows([]);
      setTotals(null);
      const unavailable = getAssetAccountingUnavailable(err);
      setError(unavailable?.message || 'Det gick inte att hämta tillgångsnoten. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    const id = selectedYear?.fiscal_year_id ?? selectedYear?.id;
    if (id) fetchReport(id);
  }, [selectedYear, fetchReport]);

  const yearOptions = useMemo(() => fiscalYears.map((fy) => ({
    value: fy.fiscal_year_id ?? fy.id,
    label: `${fy.year} (${formatDate(fy.start_date)} – ${formatDate(fy.end_date)})`,
    fiscalYear: fy,
  })), [fiscalYears]);

  const selectedOption = yearOptions.find(
    (o) => o.value === (selectedYear?.fiscal_year_id ?? selectedYear?.id)
  ) || null;

  // Summera klient-side som fallback om backend inte skickar totals.
  const totalsRow = useMemo(() => {
    if (totals) return totals;
    const sums = {};
    COLUMNS.forEach((column) => {
      sums[column.key] = rows.reduce((acc, row) => acc + num(row[column.key]), 0);
    });
    return sums;
  }, [totals, rows]);

  const handleExportCsv = () => {
    const header = ['Kategori', ...COLUMNS.map((c) => c.label)].map(toCsvValue).join(';');
    const lines = rows.map((row) =>
      [toCsvValue(categoryLabel(row)), ...COLUMNS.map((c) => csvNumber(row[c.key]))].join(';')
    );
    const totalsLine = ['Summa', ...COLUMNS.map((c) => csvNumber(totalsRow[c.key]))].join(';');
    downloadCsv(
      [header, ...lines, totalsLine],
      `tillgangsnot-${selectedYear?.year || new Date().getFullYear()}.csv`
    );
  };

  if (loadingYears) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: 400 }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Laddar räkenskapsår…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-3">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h3 style={{ fontWeight: 700, color: 'var(--title)', marginBottom: 2 }}>Tillgångsnot</h3>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
            Notunderlag för årsredovisningen — anskaffningsvärden och avskrivningar
            per kategori, i årsredovisningens form.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div style={{ minWidth: 280 }}>
            <Select
              options={yearOptions}
              value={selectedOption}
              onChange={(option) => setSelectedYear(option ? option.fiscalYear : null)}
              placeholder="Välj räkenskapsår"
              theme={brandSelectTheme}
              isSearchable={false}
            />
          </div>
          <Button
            variant="outline-primary"
            className="d-flex align-items-center gap-1"
            onClick={handleExportCsv}
            disabled={rows.length === 0}
          >
            <Download size={16} /> Exportera CSV
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {fiscalYears.length === 0 && !error && (
        <p className="text-muted">
          Inga räkenskapsår hittades — skapa ett räkenskapsår under Inställningar först.
        </p>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <div className="modern-card">
          <div style={{ padding: '0.5rem 0.5rem', overflowX: 'auto' }}>
            {rows.length === 0 && !error ? (
              <p className="text-muted text-center py-5 mb-0">
                Inga tillgångar för det valda räkenskapsåret — noten byggs upp
                automatiskt från anläggningsregistret.
              </p>
            ) : (
              <Table hover responsive size="sm" className="align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ whiteSpace: 'nowrap' }}>Kategori</th>
                    {COLUMNS.map((column) => (
                      <th key={column.key} className="text-end" style={{ whiteSpace: 'nowrap' }}>
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.category_code || row.category || index}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {categoryLabel(row)}
                      </td>
                      {COLUMNS.map((column) => (
                        <td key={column.key} className="text-end" style={{ whiteSpace: 'nowrap' }}>
                          {formatNumber(num(row[column.key]))}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 700, borderTop: '2px solid #e2e8f0' }}>
                    <td>Summa</td>
                    {COLUMNS.map((column) => (
                      <td key={column.key} className="text-end">
                        {formatNumber(num(totalsRow[column.key]))}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </Table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetNoteReport;
